/* eslint-disable prettier/prettier */
import { FunctionComponent, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Sidebar } from "primereact/sidebar";
import * as XLSX from "xlsx";
import MersInvestorService from "services/mers-investor-service";
import { setToaster } from "common/toastStore";

interface MersInvestorRow {
  id: number;
  name: string;
  servicerId: string;
  subServicerOrgId: string;
  investorId: string;
}

interface MersInvestorForm {
  id: number | null;
  name: string;
  servicerId: string;
  subServicerOrgId: string;
  investorId: string;
}

const DEFAULT_FORM: MersInvestorForm = {
  id: null,
  name: "",
  servicerId: "",
  subServicerOrgId: "",
  investorId: "",
};

const normalizeText = (value: string) => value.trim().toLowerCase();
const keepDigits = (value: string) => value.replace(/\D/g, "");

const mapRow = (row: any): MersInvestorRow => ({
  id: Number(row?.id ?? row?.Id ?? 0),
  name: row?.name ?? row?.Name ?? "",
  servicerId: row?.servicerId ?? row?.ServicerId ?? "",
  subServicerOrgId: row?.subServicerOrgId ?? row?.SubServicerOrgId ?? "",
  investorId: row?.investorId ?? row?.InvestorId ?? "",
});

const ManageInvestors: FunctionComponent = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [rows, setRows] = useState<MersInvestorRow[]>([]);
  const [addVisible, setAddVisible] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [form, setForm] = useState<MersInvestorForm>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadMersInvestors = () => {
    setLoading(true);
    MersInvestorService.getMersInvestors()
      .then((response: any) => {
        const data = Array.isArray(response?.data) ? response.data : [];
        setRows(data.map(mapRow));
      })
      .catch((error: Error) => {
        console.log(error);
        setToaster({
          severity: "error",
          summary: "Load failed",
          detail: "Unable to load investors.",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadMersInvestors();
  }, []);

  const openAddDrawer = () => {
    setIsEditMode(false);
    setForm(DEFAULT_FORM);
    setErrors({});
    setAddVisible(true);
  };

  const openEditDrawer = (rowData: MersInvestorRow) => {
    setIsEditMode(true);
    setForm({
      id: rowData.id,
      name: rowData.name,
      servicerId: rowData.servicerId,
      subServicerOrgId: rowData.subServicerOrgId,
      investorId: rowData.investorId,
    });
    setErrors({});
    setAddVisible(true);
  };

  const closeDrawer = () => {
    setAddVisible(false);
    setIsEditMode(false);
    setForm(DEFAULT_FORM);
    setErrors({});
  };

  const hasDuplicateName = () => {
    const normalizedName = normalizeText(form.name);
    if (!normalizedName) return false;

    return rows.some((row) => {
      if (row.id === form.id) return false;
      return normalizeText(row.name) === normalizedName;
    });
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!form.name.trim()) nextErrors.name = "Name is required.";

    if (form.servicerId.trim() && !/^\d+$/.test(form.servicerId.trim())) {
      nextErrors.servicerId = "Servicer Id must be numeric.";
    }

    if (
      form.subServicerOrgId.trim() &&
      !/^\d+$/.test(form.subServicerOrgId.trim())
    ) {
      nextErrors.subServicerOrgId = "Sub Servicer Org Id must be numeric.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    if (hasDuplicateName()) {
      setToaster({
        severity: "warn",
        summary: "Duplicate name",
        detail: "An investor with the same Name already exists.",
      });
      return;
    }

    const payload = {
      Id: form.id || 0,
      Name: form.name.trim(),
      ServicerId: form.servicerId.trim(),
      SubServicerOrgId: form.subServicerOrgId.trim(),
      InvestorId: form.investorId.trim(),
    };

    setSaving(true);

    try {
      if (isEditMode) {
        await MersInvestorService.updateMersInvestor(payload);
      } else {
        await MersInvestorService.createMersInvestor(payload);
      }

      setToaster({
        severity: "success",
        summary: isEditMode ? "Updated" : "Created",
        detail: `Investor ${isEditMode ? "updated" : "created"} successfully.`,
      });
      closeDrawer();
      loadMersInvestors();
    } catch (error: any) {
      console.log(error);
      setToaster({
        severity: "error",
        summary: isEditMode ? "Update failed" : "Create failed",
        detail:
          error?.response?.data ||
          `Unable to ${isEditMode ? "update" : "create"} investor.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const renderActions = (rowData: MersInvestorRow) => {
    return (
      <Button
        label="Edit"
        icon="pi pi-pencil"
        className="p-button-outlined p-button-sm"
        onClick={() => openEditDrawer(rowData)}
      />
    );
  };

  const exportToExcel = () => {
    if (!rows.length) {
      setToaster({
        severity: "info",
        summary: "No data",
        detail: "No records to export.",
      });
      return;
    }

    const exportRows = rows.map((row) => ({
      Name: row.name,
      "Investor Id": row.investorId,
      "Servicer Id": row.servicerId,
      "Sub Servicer Org Id": row.subServicerOrgId,
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Investors");
    XLSX.writeFile(wb, `manage_investors_export_${new Date().toISOString()}.xlsx`);
  };

  const renderHeader = () => {
    return (
      <div className="flex align-items-center gap-3 w-full">
        <div>
          <h2 className="m-0 text-xl font-semibold">Manage Investors</h2>
          <p className="m-0 text-sm text-500">Manage Mers investors.</p>
        </div>

        <div className="ml-auto flex gap-2">
          <Button
            label="Export to Excel"
            icon="pi pi-file-excel"
            className="p-button-outlined"
            onClick={exportToExcel}
          />
          <Button
            label="Add Investor"
            icon="pi pi-plus"
            className="p-button-success"
            onClick={openAddDrawer}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <DataTable
        className="shadow-md"
        value={rows}
        loading={loading}
        stripedRows
        responsiveLayout="scroll"
        emptyMessage="No investors found."
        header={renderHeader}
        dataKey="id"
      >
        <Column field="name" header="Name" />
        <Column field="investorId" header="Investor Id" />
        <Column field="servicerId" header="Servicer Id" />
        <Column field="subServicerOrgId" header="Sub Servicer Org Id" />
        <Column body={renderActions} header="Actions" style={{ width: "10rem" }} />
      </DataTable>

      <Sidebar
        visible={addVisible}
        position="right"
        style={{ width: "38rem" }}
        onHide={closeDrawer}
      >
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="m-0 text-lg font-semibold">
              {isEditMode ? "Edit Investor" : "Add Investor"}
            </h3>
            <p className="m-0 text-sm text-500">
              {isEditMode
                ? "Update the existing investor details."
                : "Create a new investor entry."}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="mers-investor-name">Name</label>
            <InputText
              id="mers-investor-name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              className={errors.name ? "p-invalid" : ""}
            />
            {errors.name && <small className="p-error">{errors.name}</small>}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="mers-investor-investor-id">Investor Id</label>
            <InputText
              id="mers-investor-investor-id"
              value={form.investorId}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  investorId: e.target.value,
                }))
              }
              maxLength={50}
              className={errors.investorId ? "p-invalid" : ""}
            />
            {errors.investorId && (
              <small className="p-error">{errors.investorId}</small>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="mers-investor-servicer-id">Servicer Id</label>
            <InputText
              id="mers-investor-servicer-id"
              value={form.servicerId}
              inputMode="numeric"
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  servicerId: keepDigits(e.target.value),
                }))
              }
              className={errors.servicerId ? "p-invalid" : ""}
            />
            {errors.servicerId && (
              <small className="p-error">{errors.servicerId}</small>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="mers-investor-sub-servicer-org-id">
              Sub Servicer Org Id
            </label>
            <InputText
              id="mers-investor-sub-servicer-org-id"
              value={form.subServicerOrgId}
              inputMode="numeric"
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  subServicerOrgId: keepDigits(e.target.value),
                }))
              }
              className={errors.subServicerOrgId ? "p-invalid" : ""}
            />
            {errors.subServicerOrgId && (
              <small className="p-error">{errors.subServicerOrgId}</small>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              label="Cancel"
              className="p-button-outlined"
              onClick={closeDrawer}
              disabled={saving}
            />
            <Button
              label={isEditMode ? "Update" : "Save"}
              icon={saving ? "pi pi-spin pi-spinner" : "pi pi-check"}
              onClick={handleSave}
              disabled={saving}
            />
          </div>
        </div>
      </Sidebar>
    </>
  );
};

export default ManageInvestors;